import { useEffect, useState } from 'react';
import { ComponentConfig } from '@measured/puck';
import { DropZone } from '@measured/puck';
import { CartTotal } from '../../../../page-builder/components/cart/cart-total/CartTotal';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { FieldLabel } from '@measured/puck';
import { Switch } from '@mui/material';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import './styles.scss';
import { ReduxSelectorProps } from '../../../services/constants';
import InputField from '../../../../components/common/input-field';
import CustomTooltip from '../../../../components/common/tooltip';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getNumberPattern,
  getPaddingStyles,
  getPaddingStylesForColumns,
} from '../../../utils/helper';
import ColorPicker from '../../../puck/reusable-props/ColorPicker';
import './../../text-editor/style.scss';
import { QuillTextEditor } from '../../../puck/reusable-props/quill-editor';
import {
  paddingFields,
  paddingProps,
  PaddingType,
} from '../../../puck/reusable-props/padding';
import {
  marginFields,
  marginProps,
  MarginType,
} from '../../../puck/reusable-props/margin';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import Column, {
  columnProps,
  IColumnsProps,
} from '../../../puck/reusable-props/Column';

export type StandardLayoutProps = {
  border: boolean;
  bottomOfContainer: boolean;
  ennableSticky: 'none' | 'auto' | 'left' | 'right';
  gap: string;
  leftColumn: IColumnsProps;
  rightColumn: IColumnsProps;
  bgColorLeft: string;
  bgColorRight: string;
  borderColor: string;
  orderSummary: string;
  borderSummary: boolean;
  orderSummaryCartTotal: boolean;
  orderSummaryCartTotalColor: string;
  iconColor: string;
  customize: {
    currentDevice: 'mobile' | 'tablet' | 'desktop';
    showAccordion: {
      mobile: {
        show: boolean;
        defaultCollapsed: boolean;
      };
      tablet: {
        show: boolean;
        defaultCollapsed: boolean;
      };
      desktop: {
        show: boolean;
        defaultCollapsed: boolean;
      };
    };
  };
  headerBackgroundColor: {
    color: string;
    isInherit: boolean;
  };
  headerPadding: PaddingType;
  headerMargin: MarginType;
  headerBorder: IBorderValueProps;
};

export const StandardLayout = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<StandardLayoutProps> => {
  return {
    label: (
      <CustomTooltip title="StandardLayout">
        <span>Standard Layout</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      leftColumn: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Column onChange={onChange} value={value} title="Left Column" />
        ),
      },
      rightColumn: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Column onChange={onChange} value={value} title="Right Column" />
        ),
      },
      bottomOfContainer: {
        type: 'radio',
        label: 'Divider Bottom Of Container',
        options: [
          { label: 'True', value: true },
          { label: 'False', value: false },
        ],
      },
      gap: {
        type: 'custom',
        label: 'Gap',
        render: ({ value, onChange, field }) => (
          <InputField
            value={value}
            field={field}
            onChange={e => {
              const value = getNumberPattern(e);
              if (value !== null) {
                onChange(value);
              }
            }}
            className="h-[42px] mt-3"
          />
        ),
      },
      ennableSticky: {
        type: 'select',
        label: 'Position Sticky',
        options: [
          { label: 'None', value: 'none' },
          { label: 'Auto', value: 'auto' },
          { label: 'Left', value: 'left' },
          { label: 'Right', value: 'right' },
        ],
      },
      border: {
        type: 'radio',
        label: 'Border',
        options: [
          { label: 'True', value: true },
          { label: 'False', value: false },
        ],
      },
      borderColor: {
        type: 'custom',
        label: 'Border Color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      bgColorLeft: {
        type: 'custom',
        label: 'Background Color(Left Column)',
        render: ({ value, onChange, field, name }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={name}
          />
        ),
      },
      bgColorRight: {
        type: 'custom',
        label: 'Background Color(Right Column)',
        render: ({ value, onChange, field, name }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={name}
          />
        ),
      },
      borderSummary: {
        type: 'radio',
        label: 'Border Summary',
        options: [
          { label: 'True', value: true },
          { label: 'False', value: false },
        ],
      },
      customize: {
        type: 'custom',
        render: ({ value, onChange }) => {
          const updateShowAccordion = (field: string, checked: boolean) => {
            onChange({
              ...value,
              showAccordion: {
                ...value.showAccordion,
                [value.currentDevice]: {
                  ...value.showAccordion[value.currentDevice],
                  [field]: checked,
                },
              },
            });
          };
          return (
            <div className="grid gap-2">
              <FieldLabel
                label={
                  (
                    <CustomTooltip title="device">
                      <span>Device</span>
                    </CustomTooltip>
                  ) as React.ReactNode as string
                }
                el="div">
                <ToggleButtonGroup
                  value={value?.currentDevice}
                  exclusive
                  className="w-full"
                  onChange={(_e, currentDevice) =>
                    currentDevice && onChange({ ...value, currentDevice })
                  }>
                  <ToggleButton
                    size="small"
                    disableRipple
                    value="mobile"
                    className="flex-1">
                    Mobile
                  </ToggleButton>
                  <ToggleButton
                    size="small"
                    value="tablet"
                    disableRipple
                    className="flex-1">
                    Tablet
                  </ToggleButton>
                  <ToggleButton
                    size="small"
                    value="desktop"
                    disableRipple
                    className="flex-1">
                    Desktop
                  </ToggleButton>
                </ToggleButtonGroup>
              </FieldLabel>
              <div className="flex gap-2 items-center justify-between">
                <label>Show Accordion</label>
                <Switch
                  checked={value?.showAccordion?.[value?.currentDevice]?.show}
                  onChange={event =>
                    updateShowAccordion('show', event.target.checked)
                  }
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
              {value?.showAccordion?.[value?.currentDevice]?.show && (
                <div className="flex gap-2 items-center justify-between">
                  <label>Default Collapsed</label>
                  <Switch
                    checked={
                      value?.showAccordion[value.currentDevice].defaultCollapsed
                    }
                    onChange={event =>
                      updateShowAccordion(
                        'defaultCollapsed',
                        event.target.checked,
                      )
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              )}
            </div>
          );
        },
      },
      orderSummary: {
        ...QuillTextEditor('Order Summary').content,
      },
      headerMargin: {
        ...marginFields.margin,
      },
      headerPadding: {
        ...paddingFields.padding,
      },
      headerBorder: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      orderSummaryCartTotal: {
        type: 'radio',
        label: 'Mobile Summary Cart Total',
        options: [
          { label: 'True', value: true },
          { label: 'False', value: false },
        ],
      },
      orderSummaryCartTotalColor: {
        type: 'custom',
        label: 'Order Summary Cart Total Color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      iconColor: {
        type: 'custom',
        label: 'Icon Color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      headerBackgroundColor: {
        type: 'custom',
        label: 'Background Color',
        render: ({ value, onChange }) => (
          <>
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Inherit Parent</p>
              <Switch
                checked={value?.isInherit}
                onChange={e =>
                  onChange({
                    ...value,
                    isInherit: e.target.checked,
                  })
                }
              />
            </div>
            {!value?.isInherit && (
              <ColorPicker
                label="Background Color"
                tooltip="backgroundColor"
                onChange={color =>
                  onChange({
                    ...value,
                    color,
                    isInherit: value?.isInherit ?? false,
                  })
                }
                value={value?.color}
              />
            )}
          </>
        ),
      },
    },
    defaultProps: {
      border: true,
      bottomOfContainer: true,
      ennableSticky: 'auto',
      gap: '10',
      borderColor: '#FFFFFF',
      bgColorLeft: '#FFFFFF',
      bgColorRight: '#FFFFFF',
      orderSummaryCartTotal: true,
      borderSummary: false,
      orderSummary: `<p style="line-height:1.5;"><strong style="font-size: 20px;">Show order summary</strong></p>`,
      customize: {
        currentDevice: 'desktop',
        showAccordion: {
          mobile: {
            show: false,
            defaultCollapsed: false,
          },
          tablet: {
            show: false,
            defaultCollapsed: false,
          },
          desktop: {
            show: false,
            defaultCollapsed: false,
          },
        },
      },
      leftColumn: columnProps,
      rightColumn: columnProps,
      headerBackgroundColor: {
        color: '#FFFFFF',
        isInherit: false,
      },
      headerPadding: paddingProps.padding,
      headerMargin: marginProps.margin,

      headerBorder: borderProps.border,
      orderSummaryCartTotalColor: '#000000',
      iconColor: '#000000',
    },
    render: ({
      border,
      bottomOfContainer,
      ennableSticky,
      gap,
      bgColorLeft,
      bgColorRight,
      borderColor,
      orderSummary,
      borderSummary,
      orderSummaryCartTotal,
      customize,
      editMode,
      orderSummaryCartTotalColor,
      iconColor,
      headerMargin,
      headerBorder,
      headerPadding,
      leftColumn,
      rightColumn,
      headerBackgroundColor,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const [dropDown, setDropDown] = useState<boolean>(
        !customize?.showAccordion?.[currentDeviceType]?.defaultCollapsed,
      );
      const toggleDropZone = () => setDropDown(!dropDown);
      const headerMarginStyles = getMarginStyles(
        headerMargin,
        currentDeviceType,
      );

      let leftWidth: string;
      if (leftColumn?.width?.[currentDeviceType]?.includes('%')) {
        leftWidth = leftColumn?.width?.[currentDeviceType];
      } else {
        leftWidth = `${leftColumn?.width?.[currentDeviceType]}px`;
      }

      let rightWidth: string;
      if (rightColumn?.width?.[currentDeviceType]?.includes('%')) {
        rightWidth = rightColumn?.width?.[currentDeviceType];
      } else {
        rightWidth = `${rightColumn?.width?.[currentDeviceType]}px`;
      }

      useEffect(() => {
        setDropDown(
          !customize?.showAccordion[currentDeviceType]?.defaultCollapsed,
        );
      }, [customize, currentDeviceType]);
      const isSticky = ['left', 'auto'].includes(ennableSticky);

      return (
        <div
          className="grid lg:grid-cols-2 lg:grid-rows-[auto_1fr_auto] bg-[var(--template-background-color)] min-h-screen"
          style={{ columnGap: isNaN(+gap) ? gap : +gap }}>
          <DropZone zone="top" disallow={['StandardLayout', 'SplitLayout']} />
          <div
            className={`lg:row-span-3 ${border ? 'lg-max:border-l-0 border-l-2' : ''} ${isSticky ? 'lg:sticky lg:top-0' : ''}`}
            style={{
              backgroundColor: bgColorRight,
              borderColor: borderColor,
              height: bottomOfContainer ? '100%' : 'fit-content',
            }}>
            {customize?.showAccordion?.[currentDeviceType]?.show ? (
              <>
                <div
                  className={`flex justify-between ${borderSummary ? 'border-t-2 border-b-2 p-2 border-black-500' : ''} text-editor-content`}
                  style={{
                    backgroundColor: headerBackgroundColor?.isInherit
                      ? 'inherit'
                      : headerBackgroundColor?.color,
                    ...headerMarginStyles,
                    ...getBorderProps(headerBorder, currentDeviceType),
                    ...getPaddingStyles(headerPadding, currentDeviceType),
                  }}>
                  <button
                    type="button"
                    className="flex items-center"
                    onClick={toggleDropZone}>
                    <div
                      dangerouslySetInnerHTML={{ __html: orderSummary }}
                      style={{ lineHeight: 1.5 }}
                    />
                    <ChevronDownIcon
                      className={`h-5 w-5 ml-2 transition-transform duration-200 ${dropDown ? 'transform rotate-180' : ''}`}
                      style={{ color: iconColor }}
                      aria-hidden="true"
                    />
                  </button>
                  {orderSummaryCartTotal && (
                    <CartTotal
                      useAppSelector={useAppSelector}
                      style={{ color: orderSummaryCartTotalColor }}
                    />
                  )}
                </div>
                {(dropDown || editMode) && (
                  <DropZone
                    zone="right"
                    disallow={['StandardLayout', 'SplitLayout']}
                    className={`${rightColumn?.marginAuto?.[currentDeviceType] ? 'm-auto' : ''}`}
                    style={{
                      width: rightWidth,
                      ...getPaddingStylesForColumns(
                        rightColumn,
                        currentDeviceType,
                      ),
                    }}
                  />
                )}
              </>
            ) : (
              <DropZone
                disallow={['StandardLayout', 'SplitLayout']}
                zone="right"
                className={`${rightColumn?.marginAuto?.[currentDeviceType] ? 'm-auto' : ''}`}
                style={{
                  width: rightWidth,
                  ...getPaddingStylesForColumns(rightColumn, currentDeviceType),
                }}
              />
            )}
          </div>
          <DropZone
            zone="left"
            style={{
              backgroundColor: bgColorLeft,
              height: `${isSticky ? 'fit-content' : ''}`,
              width: leftWidth,
              ...getPaddingStylesForColumns(leftColumn, currentDeviceType),
            }}
            disallow={['StandardLayout', 'SplitLayout']}
            className={`place-last ${isSticky ? 'lg:sticky lg:top-0' : ''} ${leftColumn?.marginAuto?.[currentDeviceType] ? 'm-auto' : ''}`}
          />
          <DropZone
            disallow={['StandardLayout', 'SplitLayout']}
            zone="footer"
            style={{
              width: leftWidth,
              ...getPaddingStylesForColumns(leftColumn, currentDeviceType),
            }}
            className={`place-last ${leftColumn?.marginAuto?.[currentDeviceType] ? 'm-auto' : ''}`}
          />
        </div>
      );
    },
  };
};
